import { useState, useEffect, useMemo, Fragment } from 'react';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { Switch } from '@/components/ui-kit/switch';
import { useStepper } from '@/components/stepper/stepper-provider';
import { useCreateApiContext } from '../create-api-context';
import { Plus, Trash2, ChevronDown, ChevronRight, PlusCircle } from 'lucide-react';
import { showErrorToast } from '@/hooks/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui-kit/table';
import { ChipsInput, ChipsInputField, ChipsInputList } from '@/components/chip-input/chips-input';
import { ParameterFormField } from './utils/tree-utils';
import { SYSTEM_VARIABLE_OPTIONS } from '@/modules/gpt-chats/pages/tools/tool-api-details/create-api/forms/utils/system-variable-utils';
import { useParameterTree } from './hooks/use-parameter-tree';
import { buildParameters, reconstructTree } from './utils/schema-utils';
import { validateParameterName } from './utils/naming-utils';
import { useTranslation } from 'react-i18next';

const nestedObjectLevel = 3;

export const CreateApiInputParametersForm = () => {
  const { nextStep } = useStepper();
  const { t } = useTranslation();
  const { formData, updateFormData } = useCreateApiContext();
  const [parameterErrors, setParameterErrors] = useState<Record<string, string>>({});

  const {
    parameters,
    setParameters,
    handleAddParameter,
    handleAddChildParameter,
    handleRemoveParameter,
    handleParameterChange,
    toggleExpand,
  } = useParameterTree([
    {
      id: '1',
      name: '',
      description: '',
      type: 'string',
      location: 'body',
      defaultValue: '',
      required: false,
    },
  ]);

  const namingErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    const validate = (params: ParameterFormField[], parentType = '') => {
      params.forEach((p) => {
        if (parentType !== 'array') {
          const err = validateParameterName(p.name, p.location);
          if (err) errors[p.id] = t(err.message as any);
        }
        if (p.children) validate(p.children, p.type);
      });
    };
    validate(parameters);
    return errors;
  }, [parameters, t]);

  const hasNamingErrors = Object.keys(namingErrors).length > 0;

  useEffect(() => {
    if (formData.input_parameters && Object.keys(formData.input_parameters).length > 0) {
      const loadedParams = reconstructTree(formData.input_parameters);

      if (loadedParams.length > 0) {
        setParameters(loadedParams);
      }
    }
  }, [formData.input_parameters, setParameters]);

  const handleSubmit = () => {
    // Check for duplicates in non-empty parameters
    const validParams = parameters.filter((p) => p.name && p.name.trim() !== '');
    const uniqueNames = new Set(validParams.map((p) => p.name));

    if (uniqueNames.size !== validParams.length) {
      showErrorToast({
        errors: { error: t('PARAMETER_NAMES_UNIQUE') },
      });
      return;
    }

    setParameterErrors({});
    const inputParameters = buildParameters(parameters, false);
    updateFormData({ input_parameters: inputParameters });
    nextStep();
  };

  const renderRows = (
    params: ParameterFormField[],
    level = 0,
    objectDepth = 0,
    parentType = ''
  ) => {
    return params.map((param, index) => {
      const isArrayItem = parentType === 'array';

      return (
        <Fragment key={param.id}>
          <TableRow
            className={`border-b-0 hover:bg-transparent ${
              level === 0 && index > 0 ? '!border-t !border-border' : ''
            }`}
          >
            <TableCell>
              <div className="flex items-center gap-2" style={{ paddingLeft: `${level * 40}px` }}>
                {param.type === 'array' || param.type === 'object' ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 p-0"
                    onClick={() => toggleExpand(param.id)}
                  >
                    {param.isExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </Button>
                ) : null}
                <div className="flex-1 space-y-1">
                  <Input
                    placeholder={isArrayItem ? t('ARRAY_ITEM') : t('ENTER_NAME')}
                    value={isArrayItem ? 'item' : param.name}
                    disabled={isArrayItem}
                    maxLength={100}
                    onChange={(e) => handleParameterChange(param.id, 'name', e.target.value)}
                    className={parameterErrors[param.id] ? 'border-error' : ''}
                  />
                  {parameterErrors[param.id] && (
                    <p className="text-xs text-error">{parameterErrors[param.id]}</p>
                  )}
                  {namingErrors[param.id] && (
                    <p className="text-xs text-error">{namingErrors[param.id]}</p>
                  )}
                  {!isArrayItem && param.name.length >= 100 && (
                    <p className="text-[12px] text-error">{t('MAX_CHARACTERS', { count: 100 })}</p>
                  )}
                </div>
              </div>
            </TableCell>
            <TableCell>
              <div className="space-y-1">
                <Input
                  placeholder={t('ENTER_DESCRIPTION')}
                  value={param.description}
                  maxLength={100}
                  onChange={(e) => handleParameterChange(param.id, 'description', e.target.value)}
                />
                {(param.description || '').length >= 100 && (
                  <p className="text-[12px] text-error">{t('MAX_CHARACTERS', { count: 100 })}</p>
                )}
              </div>
            </TableCell>
            <TableCell>
              <Select
                value={param.type}
                onValueChange={(value) => handleParameterChange(param.id, 'type', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('SELECT')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="string">String</SelectItem>
                  <SelectItem value="number">Number</SelectItem>
                  <SelectItem value="integer">Integer</SelectItem>
                  <SelectItem value="boolean">Boolean</SelectItem>
                  {level < 10 && !isArrayItem && <SelectItem value="array">Array</SelectItem>}
                  {objectDepth < nestedObjectLevel && (
                    <SelectItem value="object">Object</SelectItem>
                  )}

                  {!isArrayItem && (
                    <>
                      <SelectSeparator />
                      <SelectItem value="variable">System Variable</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </TableCell>
            <TableCell>
              <Select
                value={param.location}
                onValueChange={(value) => handleParameterChange(param.id, 'location', value)}
                disabled={level > 0}
              >
                <SelectTrigger className={level > 0 ? 'opacity-50' : ''}>
                  <SelectValue placeholder={t('SELECT')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="query">Query</SelectItem>
                  <SelectItem value="body">Body</SelectItem>
                  <SelectItem value="header">Header</SelectItem>
                  <SelectItem value="path">Path</SelectItem>
                </SelectContent>
              </Select>
            </TableCell>
            <TableCell>
              {isArrayItem ? (
                param.type !== 'object' && (
                  <div className="w-full">
                    <ChipsInput
                      value={(() => {
                        try {
                          const parsed = param.defaultValue ? JSON.parse(param.defaultValue) : [];
                          return Array.isArray(parsed) ? parsed : [];
                        } catch {
                          return [];
                        }
                      })()}
                      onChange={(val) =>
                        handleParameterChange(param.id, 'defaultValue', JSON.stringify(val))
                      }
                      className="max-h-[100px] min-h-[40px] overflow-y-auto"
                    >
                      <ChipsInputList />
                      <ChipsInputField />
                    </ChipsInput>
                  </div>
                )
              ) : param.type === 'variable' ? (
                <Select
                  value={param.defaultValue || SYSTEM_VARIABLE_OPTIONS[0].value}
                  onValueChange={(value) => handleParameterChange(param.id, 'defaultValue', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('SELECT_VARIABLE')} />
                  </SelectTrigger>
                  <SelectContent>
                    {SYSTEM_VARIABLE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                param.type !== 'array' &&
                param.type !== 'object' && (
                  <Input
                    placeholder={t('ENTER_DEFAULT_VALUE')}
                    value={param.defaultValue}
                    onChange={(e) =>
                      handleParameterChange(param.id, 'defaultValue', e.target.value)
                    }
                  />
                )
              )}
            </TableCell>
            <TableCell>
              <Switch
                checked={param.required}
                onCheckedChange={(checked) => handleParameterChange(param.id, 'required', checked)}
              />
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveParameter(param.id)}
                  disabled={(parameters.length === 1 && level === 0) || isArrayItem}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
                {param.type === 'object' && level < 10 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleAddChildParameter(param.id, param.location)}
                    className="text-primary"
                  >
                    <PlusCircle className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </TableCell>
          </TableRow>

          {(param.type === 'object' || param.type === 'array') &&
            param.isExpanded &&
            param.children &&
            renderRows(
              param.children,
              level + 1,
              param.type === 'object' ? objectDepth + 1 : objectDepth,
              param.type
            )}
        </Fragment>
      );
    });
  };

  return (
    <div className="w-full">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold">{t('INPUT_PARAMETERS')}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t('INPUT_PARAMETERS_DESCRIPTION')}</p>
      </div>

      <div className="rounded-lg border bg-background">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-[180px]">{t('PARAM_NAME')}</TableHead>
              <TableHead className="w-[200px]">{t('DESCRIPTION')}</TableHead>
              <TableHead className="w-[140px]">{t('TYPE')}</TableHead>
              <TableHead className="w-[140px]">{t('LOCATION')}</TableHead>
              <TableHead className="w-[180px]">{t('DEFAULT_VALUE')}</TableHead>
              <TableHead className="w-[100px]">{t('REQUIRED')}</TableHead>
              <TableHead className="w-[80px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>{renderRows(parameters)}</TableBody>
        </Table>

        <div className="p-6 pt-2">
          <Button variant="ghost" onClick={handleAddParameter} className="text-primary">
            <Plus className="mr-2 h-4 w-4" />
            {t('ADD_PARAMETER')}
          </Button>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-start gap-3">
        <Button onClick={handleSubmit} disabled={hasNamingErrors} className="px-8">
          {t('CONTINUE')}
        </Button>
      </div>
    </div>
  );
};
